import React, { useState, useContext } from 'react';
import {
  Button,
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  FormHelperText,
  Switch,
  Tooltip,
  IconButton,
} from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import PublishIcon from '@material-ui/icons/Publish';
import PropTypes from 'prop-types';

import { CommonDialog } from 'components';
import ConfirmModal from 'Views/Common/ConfirmModal';
import { Context } from 'context';
import { ADD_NOTIFICATION } from 'context/actions';
import { SSO_SERVICES } from 'Services';
import Utils from 'Shared/Utils';

import {
  IDENTIFIER_TYPE,
  IDENTIFIER_TYPE_LABEL,
  PROVIDER_CREDENTIAL_FIELDS,
  PROVIDER_IDENTIFIER_TYPES,
  PROVIDER_TENANT_FIELD,
  SSO_PROVIDER,
  SUPPORTED_PROVIDERS,
  capitalizeProvider,
  getDefaultIdentifierType,
  providerUsesField,
} from './constants';

const MASK_CHAR = '•';

const getInitialFormState = (configData) => ({
  sso_provider: configData?.sso_provider || SSO_PROVIDER.MICROSOFT,
  client_id: configData?.masked_client_id || '',
  client_secret: '',
  // Microsoft only
  tenant_id: configData?.masked_tenant_id || '',
  // Google only — the service account key is never returned, not even masked
  service_account_key: '',
  workspace_admin_email: configData?.masked_workspace_admin_email || '',
  allowed_email_domains: configData?.allowed_email_domains?.join(', ') || '',
  jit_enabled: configData?.jit_enabled ?? true,
  jit_update_on_login: configData?.jit_update_on_login ?? true,
  auto_detect_mapping: configData?.auto_detect_mapping ?? false,
  idp_group_identifier_type:
    configData?.idp_group_identifier_type ||
    getDefaultIdentifierType(configData?.sso_provider || SSO_PROVIDER.MICROSOFT),
});

// A key downloaded from the Google Cloud console is a JSON object with a
// fixed shape — check it here so a wrong file is caught before saving.
// Returns an empty string when the key is usable, otherwise the reason.
const getServiceAccountError = (contents) => {
  let parsed;
  try {
    parsed = JSON.parse(contents);
  } catch (error) {
    // Not surfaced: V8 parse messages can quote a span of the input, and
    // the input here is a private key.
    console.debug('Service account key failed to parse:', error.message);
    return 'That is not valid JSON. Upload the key file downloaded from Google Cloud.';
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return 'The service account key must be a JSON object.';
  }
  if (parsed.type !== 'service_account') {
    return 'That JSON file is not a service account key (expected "type": "service_account").';
  }
  if (!parsed.client_email || !parsed.private_key) {
    return 'The service account key is missing client_email or private_key.';
  }
  return '';
};

const isMaskedValue = (value) => value?.includes(MASK_CHAR);

const getActualCredential = (value) => (isMaskedValue(value) ? '' : (value || '').trim());

// The credential half of the request body. Provider is not editable, so
// the credential shape is fixed for the lifetime of the config — only
// that provider's fields are ever sent.
const buildCredentialPayload = ({
  isEditMode,
  hasCredentialUpdate,
  credentials,
  usesTenant,
  usesServiceAccount,
}) => {
  if (isEditMode && !hasCredentialUpdate) return {};

  const payload = {
    client_id: credentials.client_id,
    client_secret: credentials.client_secret,
  };

  if (usesTenant) {
    payload.tenant_id = credentials.tenant_id;
  }

  // Sent independently: an omitted field is carried over from storage,
  // so either can be rotated without re-entering the other.
  if (usesServiceAccount) {
    if (credentials.service_account_key) {
      payload.service_account_key = credentials.service_account_key;
    }
    if (credentials.workspace_admin_email) {
      payload.workspace_admin_email = credentials.workspace_admin_email;
    }
  }

  return payload;
};

// Applies one field edit plus the resets it implies. Pure and hoisted so
// the component itself stays readable.
const applyFieldChange = (prev, field, value) => {
  const next = { ...prev, [field]: value };

  if (field === 'jit_enabled' && !value) {
    next.auto_detect_mapping = false;
  }
  if (field === 'idp_group_identifier_type' && value !== IDENTIFIER_TYPE.NAME) {
    next.auto_detect_mapping = false;
  }
  // Auto-detect is Microsoft-only for this release
  if (field === 'jit_enabled' && value && prev.sso_provider !== SSO_PROVIDER.MICROSOFT) {
    next.auto_detect_mapping = false;
  }
  if (field === 'sso_provider') {
    // Credentials and identifier types do not carry across providers
    next.tenant_id = '';
    next.service_account_key = '';
    next.workspace_admin_email = '';
    next.idp_group_identifier_type = getDefaultIdentifierType(value);
    if (value !== SSO_PROVIDER.MICROSOFT) {
      next.auto_detect_mapping = false;
    }
  }

  return next;
};

// Google's two service account fields, checked as a pair.
const getServiceAccountCredentialError = ({ credentials, hasStoredServiceAccount }) => {
  const { service_account_key, workspace_admin_email } = credentials;

  // Omitted fields fall back to what is stored, so one alone is only a
  // problem when there is no stored pair behind it.
  if (!hasStoredServiceAccount && Boolean(service_account_key) !== Boolean(workspace_admin_email)) {
    return 'Service Account Key and Workspace Admin Email must be provided together.';
  }
  if (service_account_key) {
    return getServiceAccountError(service_account_key);
  }
  return '';
};

// Empty string when the credential fields are acceptable, otherwise why not.
const getCredentialError = ({
  googleDirectoryMissing,
  isEditMode,
  hasCredentialUpdate,
  credentials,
  usesTenant,
  tenantField,
  provider,
  usesServiceAccount,
  hasStoredServiceAccount,
}) => {
  if (googleDirectoryMissing) {
    return 'Auto-Create Users requires a service account: Google group membership is read from the Admin SDK, not the ID token.';
  }

  // In edit mode credentials are optional, but a partial set is not: the
  // backend rewrites the whole stored blob from what is sent.
  if (isEditMode && !hasCredentialUpdate) return '';

  if (!credentials.client_id || !credentials.client_secret) {
    return isEditMode
      ? 'Credentials are stored as one set — re-enter Client ID and Client Secret to save this change.'
      : 'Client ID and Client Secret are required.';
  }

  if (usesTenant && !credentials.tenant_id) {
    return `${tenantField.label} is required for ${capitalizeProvider(provider)}.`;
  }

  if (usesServiceAccount) {
    return getServiceAccountCredentialError({ credentials, hasStoredServiceAccount });
  }

  return '';
};

const SSOConfigModal = ({ level, levelId, configData, onClose, onUpdate }) => {
  const { dispatch } = useContext(Context);
  const isEditMode = Boolean(configData);

  const [initialState] = useState(() => getInitialFormState(configData));
  const [formState, setFormState] = useState(initialState);
  const [actionLoading, setActionLoading] = useState(false);
  const [showIdentifierWarning, setShowIdentifierWarning] = useState(false);
  const [keyFileName, setKeyFileName] = useState('');
  const [keyFileError, setKeyFileError] = useState('');
  const [isPastingKey, setIsPastingKey] = useState(false);

  const isFormDirty = JSON.stringify(formState) !== JSON.stringify(initialState);

  const provider = formState.sso_provider;
  const isMicrosoft = provider === SSO_PROVIDER.MICROSOFT;
  const isGoogle = provider === SSO_PROVIDER.GOOGLE;

  // Which fields render and what is required comes from the provider
  // map, not from a chain of per-provider booleans — a config created
  // through the API (Okta) has to stay editable here.
  const usesTenant = providerUsesField(provider, 'tenant_id');
  const usesServiceAccount = providerUsesField(provider, 'service_account_key');
  const tenantField = PROVIDER_TENANT_FIELD[provider] || PROVIDER_TENANT_FIELD[SSO_PROVIDER.MICROSOFT];

  // An uploaded key is held in state only; the textarea is for explicit
  // paste entry, so the private key is never rendered back to the screen.
  const showKeyTextarea = isPastingKey && !keyFileName;

  const identifierTypes =
    PROVIDER_IDENTIFIER_TYPES[provider] || PROVIDER_IDENTIFIER_TYPES[SSO_PROVIDER.MICROSOFT];

  // An existing config may use a provider that is no longer offered for
  // creation; the Select still needs a matching MenuItem for its value.
  const providerOptions = SUPPORTED_PROVIDERS.includes(provider)
    ? SUPPORTED_PROVIDERS
    : [...SUPPORTED_PROVIDERS, provider];

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (field === 'service_account_key') {
      // Typed content is no longer the uploaded file
      setKeyFileName('');
      setKeyFileError('');
    }
    if (field === 'sso_provider') {
      setKeyFileName('');
      setKeyFileError('');
      setIsPastingKey(false);
    }
    setFormState((prev) => applyFieldChange(prev, field, value));
  };

  const setNotification = (severity, message) => {
    dispatch({
      type: ADD_NOTIFICATION,
      payload: { notification: { severity, message } },
    });
  };

  const parsedDomains = formState.allowed_email_domains
    .split(',')
    .map((d) => d.trim())
    .filter(Boolean);

  // Only the fields this provider actually uses — a Microsoft config must
  // never carry Google credentials, and vice versa.
  const credentialFields =
    PROVIDER_CREDENTIAL_FIELDS[formState.sso_provider] ||
    PROVIDER_CREDENTIAL_FIELDS[SSO_PROVIDER.MICROSOFT];

  const credentials = credentialFields.reduce(
    (acc, field) => ({ ...acc, [field]: getActualCredential(formState[field]) }),
    {}
  );

  const hasCredentialUpdate = Object.values(credentials).some(Boolean);

  const hasStoredServiceAccount = Boolean(isEditMode && configData?.has_service_account_key);

  // Google reads group membership from the Admin SDK, so JIT provisioning
  // needs a service account. Without one the backend rejects activation.
  // Fields left out of an update are carried over from storage, so an
  // already-stored key still counts.
  const googleDirectoryMissing =
    usesServiceAccount &&
    formState.jit_enabled &&
    !credentials.service_account_key &&
    !hasStoredServiceAccount;

  const credentialError = getCredentialError({
    googleDirectoryMissing,
    isEditMode,
    hasCredentialUpdate,
    credentials,
    usesTenant,
    tenantField,
    provider,
    usesServiceAccount,
    hasStoredServiceAccount,
  });

  const isFormValid = () => {
    if (!formState.sso_provider) return false;
    if (parsedDomains.length === 0) return false;
    return !credentialError;
  };

  const buildPayload = () => {
    const payload = {
      ...buildCredentialPayload({
        isEditMode,
        hasCredentialUpdate,
        credentials,
        usesTenant,
        usesServiceAccount,
      }),
    };

    if (!isEditMode) {
      payload.sso_provider = formState.sso_provider;
    }

    payload.jit_enabled = formState.jit_enabled;
    payload.jit_update_on_login = formState.jit_update_on_login;
    // Auto-detect is Microsoft-only this release, and the toggle is hidden
    // for other providers — never echo a stored true back, or a Google
    // config keeps it with no way to turn it off.
    payload.auto_detect_mapping = isMicrosoft && formState.auto_detect_mapping;
    payload.idp_group_identifier_type = formState.idp_group_identifier_type;
    payload.allowed_email_domains = parsedDomains;

    return payload;
  };

  const isIdentifierTypeChanged = () =>
    isEditMode &&
    configData?.idp_group_identifier_type &&
    formState.idp_group_identifier_type !== configData.idp_group_identifier_type;

  const handleSave = async () => {
    if (isIdentifierTypeChanged()) {
      setShowIdentifierWarning(true);
      return;
    }
    await submitForm();
  };

  const submitForm = async () => {
    setShowIdentifierWarning(false);
    setActionLoading(true);
    try {
      const payload = buildPayload();
      let response;

      if (isEditMode) {
        response = await SSO_SERVICES.updateSSOConfig(configData.id, payload);
      } else {
        response = await SSO_SERVICES.createSSOConfig(level, levelId, payload);
      }

      if (Utils.checkIfSuccess(response)) {
        setNotification('success', `SSO configuration ${isEditMode ? 'updated' : 'created'} successfully`);
        if (isEditMode && response?.data?.credentials_updated) {
          const ttl = response?.data?.credentials_cache_ttl_minutes || 30;
          setNotification('info', `Credential changes may take up to ${ttl} minutes to propagate across all services.`);
        }
        onUpdate();
      } else {
        const msg = response?.data?.message || response?.message || '';
        setNotification('error', msg || 'Failed to save SSO configuration');
      }
    } catch (error) {
      console.error('Error saving SSO configuration:', error);
      setNotification('error', 'An error occurred while saving SSO configuration');
    }
    setActionLoading(false);
  };

  const handleServiceAccountFile = async (event) => {
    const file = event.target.files?.[0];
    // Reset the input so re-picking the same file still fires onChange
    event.target.value = '';
    if (!file) return;

    let contents;
    try {
      contents = (await file.text()).trim();
    } catch (error) {
      console.debug('Service account key file could not be read:', error.message);
      setKeyFileError('Could not read that file. Try again, or paste the key instead.');
      setKeyFileName('');
      return;
    }

    const keyError = getServiceAccountError(contents);
    if (keyError) {
      setKeyFileError(keyError);
      setKeyFileName('');
      return;
    }

    setKeyFileError('');
    setKeyFileName(file.name);
    setIsPastingKey(false);
    setFormState((prev) => ({ ...prev, service_account_key: contents }));
  };

  const clearServiceAccountKey = () => {
    setKeyFileName('');
    setKeyFileError('');
    setIsPastingKey(false);
    setFormState((prev) => ({ ...prev, service_account_key: '' }));
  };

  const clearMaskOnFocus = (field) => () =>
    isMaskedValue(formState[field]) && setFormState((prev) => ({ ...prev, [field]: '' }));

  const restoreMaskOnBlur = (field) => () =>
    !formState[field] &&
    initialState[field] &&
    setFormState((prev) => ({ ...prev, [field]: initialState[field] }));

  const maskedHelperText = (field) =>
    isEditMode && isMaskedValue(formState[field]) ? 'Click to enter new credentials' : '';

  return (
    <>
    <CommonDialog
      open={true}
      handleClose={onClose}
      dialogTitle={isEditMode ? 'Edit SSO Configuration' : 'Create SSO Configuration'}
      primaryBtnTxt="Save"
      handlePrimaryButtonClick={handleSave}
      disabledPrimaryButton={!isFormDirty || !isFormValid() || actionLoading}
      primaryButtonStyles={{ minWidth: 105 }}
      isLoading={actionLoading}
      secondaryBtnTxt="Cancel"
      disableSecondaryButton={actionLoading}
    >
      <Grid container spacing={3} alignItems="stretch">
        {/* Row 1: Provider + provider-specific tenant/admin field */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth variant="outlined" disabled={isEditMode}>
            <InputLabel>Provider *</InputLabel>
            <Select
              value={formState.sso_provider}
              onChange={handleChange('sso_provider')}
              label="Provider *"
              data-testid="sso-provider-select"
            >
              {providerOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {capitalizeProvider(option)}
                </MenuItem>
              ))}
            </Select>
            {isEditMode && (
              <FormHelperText>Provider cannot be changed after creation</FormHelperText>
            )}
          </FormControl>
        </Grid>

        {usesTenant && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required={!isEditMode}
              variant="outlined"
              label={tenantField.label}
              value={formState.tenant_id}
              onChange={handleChange('tenant_id')}
              onFocus={clearMaskOnFocus('tenant_id')}
              onBlur={restoreMaskOnBlur('tenant_id')}
              helperText={maskedHelperText('tenant_id') || tenantField.helperText}
              data-testid="sso-tenant-id"
            />
          </Grid>
        )}

        {usesServiceAccount && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              label="Workspace Admin Email"
              value={formState.workspace_admin_email}
              onChange={handleChange('workspace_admin_email')}
              onFocus={clearMaskOnFocus('workspace_admin_email')}
              onBlur={restoreMaskOnBlur('workspace_admin_email')}
              helperText={
                maskedHelperText('workspace_admin_email') ||
                'Workspace admin the service account impersonates when reading groups'
              }
              data-testid="sso-workspace-admin-email"
            />
          </Grid>
        )}

        {/* Row 2: Client ID + Client Secret */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required={!isEditMode}
            variant="outlined"
            label="Client ID"
            value={formState.client_id}
            onChange={handleChange('client_id')}
            onFocus={clearMaskOnFocus('client_id')}
            onBlur={restoreMaskOnBlur('client_id')}
            helperText={maskedHelperText('client_id')}
            data-testid="sso-client-id"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required={!isEditMode}
            variant="outlined"
            label="Client Secret"
            type="password"
            value={formState.client_secret}
            onChange={handleChange('client_secret')}
            placeholder={isEditMode && configData?.has_credentials ? 'Configured' : ''}
            helperText={isEditMode && !formState.client_secret ? 'Click to enter new credentials' : ''}
            data-testid="sso-client-secret"
          />
        </Grid>

        {/* Google only: service account key for Admin SDK group lookups */}
        {usesServiceAccount && (
          <Grid item xs={12}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: showKeyTextarea ? 12 : 0,
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="outlined"
                component="label"
                size="small"
                startIcon={<PublishIcon />}
                data-testid="sso-service-account-upload"
              >
                <span>{keyFileName ? 'Replace JSON Key' : 'Upload JSON Key'}</span>
                <input
                  type="file"
                  accept=".json,application/json"
                  hidden
                  onChange={handleServiceAccountFile}
                />
              </Button>

              {/* The key holds a private key — once loaded it is held in
                  state and never painted back into a visible field. */}
              {keyFileName && (
                <>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    data-testid="sso-service-account-filename"
                  >
                    {keyFileName}
                  </Typography>
                  <Button size="small" onClick={clearServiceAccountKey} data-testid="sso-service-account-clear">
                    Clear
                  </Button>
                </>
              )}

              {!keyFileName && !isPastingKey && (
                <Button size="small" onClick={() => setIsPastingKey(true)} data-testid="sso-service-account-paste">
                  Paste instead
                </Button>
              )}
            </div>

            {showKeyTextarea && (
              <TextField
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                label="Service Account Key (JSON)"
                value={formState.service_account_key}
                onChange={handleChange('service_account_key')}
                placeholder={
                  isEditMode && configData?.has_service_account_key
                    ? 'Configured — paste a new key to replace it'
                    : '{ "type": "service_account", ... }'
                }
                data-testid="sso-service-account-key"
              />
            )}
            {keyFileError && (
              <FormHelperText error data-testid="sso-service-account-file-error">
                {keyFileError}
              </FormHelperText>
            )}
            <FormHelperText style={{ display: 'flex', alignItems: 'center' }}>
              Upload or paste the downloaded JSON key. Required for Auto-Create Users.
              <Tooltip
                title="Google ID tokens carry no groups claim, so memberships are read from the Admin SDK Directory API. The service account needs domain-wide delegation for the admin.directory.group.readonly scope."
                arrow
              >
                <IconButton size="small" style={{ marginLeft: 2, padding: 2 }}>
                  <InfoIcon style={{ fontSize: '0.875rem' }} />
                </IconButton>
              </Tooltip>
            </FormHelperText>
          </Grid>
        )}

        {credentialError && isFormDirty && (
          <Grid item xs={12} style={{ paddingTop: 0 }}>
            <FormHelperText error data-testid="sso-credential-error">
              {credentialError}
            </FormHelperText>
          </Grid>
        )}

        {/* Row 3: Allowed Email Domains + Group Identifier Type */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            variant="outlined"
            label="Allowed Email Domains"
            value={formState.allowed_email_domains}
            onChange={handleChange('allowed_email_domains')}
            helperText={
              isGoogle
                ? 'Comma-separated Workspace domains. Personal Google accounts are rejected.'
                : 'Comma-separated (e.g., company.com, corp.com)'
            }
            error={isFormDirty && parsedDomains.length === 0}
            data-testid="sso-allowed-domains"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Group Identifier Type</InputLabel>
            <Select
              value={formState.idp_group_identifier_type}
              onChange={handleChange('idp_group_identifier_type')}
              label="Group Identifier Type"
              data-testid="sso-identifier-type"
            >
              {identifierTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {IDENTIFIER_TYPE_LABEL[type]}
                </MenuItem>
              ))}
            </Select>
          <FormHelperText style={{ display: 'flex', alignItems: 'center' }}>
            {isGoogle
              ? 'Match groups by ID, display name, or group email'
              : 'Match groups by ID (UUID) or display name'}
            <Tooltip
              title={
                isGoogle
                  ? "Determines which field of a Google group is matched against the mappings: its ID, its display name, or its email address. A group email survives a rename and is what a Workspace admin can readily supply."
                  : 'Determines how groups from the ID token are matched: by group ID (UUID) or by group display name.'
              }
              arrow
            >
              <IconButton size="small" style={{ marginLeft: 2, padding: 2 }}>
                <InfoIcon style={{ fontSize: '0.875rem' }} />
              </IconButton>
            </Tooltip>
          </FormHelperText>
          </FormControl>
        </Grid>

        {/* Row 4: JIT toggles */}
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={formState.jit_enabled}
                onChange={handleChange('jit_enabled')}
                color="primary"
                data-testid="sso-jit-enabled"
              />
            }
            label={
              <span style={{ display: 'flex', alignItems: 'center' }}>
                Auto-Create Users on First Login
                <Tooltip
                  title="When a user signs in with SSO for the first time, their account is automatically created with the right role and access."
                  arrow
                >
                  <IconButton size="small" style={{ marginLeft: 4 }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </span>
            }
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={formState.jit_update_on_login}
                onChange={handleChange('jit_update_on_login')}
                color="primary"
                data-testid="sso-jit-update"
              />
            }
            label={
              <span style={{ display: 'flex', alignItems: 'center' }}>
                Sync Roles on Every Login
                <Tooltip
                  title="Each time a user signs in, their role and access are updated to match their current SSO group assignments."
                  arrow
                >
                  <IconButton size="small" style={{ marginLeft: 4 }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </span>
            }
          />
        </Grid>

        {isMicrosoft && formState.jit_enabled && formState.idp_group_identifier_type === IDENTIFIER_TYPE.NAME && (
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formState.auto_detect_mapping}
                  onChange={handleChange('auto_detect_mapping')}
                  color="primary"
                  data-testid="sso-auto-detect-mapping"
                />
              }
              label={
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  Auto-Detect Group Mappings
                  <Tooltip
                    title="Role mappings are automatically created from IdP group names using naming conventions (e.g., '1011 - admins' maps to location_admin at clinic 1011)."
                    arrow
                  >
                    <IconButton size="small" style={{ marginLeft: 4 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </span>
              }
            />
          </Grid>
        )}
      </Grid>
    </CommonDialog>

    {showIdentifierWarning && (
      <ConfirmModal
        isOpen={true}
        closeModal={() => setShowIdentifierWarning(false)}
        emitConfirmEvent={submitForm}
        isLoading={actionLoading}
        options={{
          title: 'Change Identifier Type',
          description:
            'This may invalidate existing group mappings. Are you sure you want to continue?',
          confirmText: 'Confirm',
          cancelText: 'Cancel',
        }}
      />
    )}
  </>
  );
};

SSOConfigModal.propTypes = {
  level: PropTypes.string.isRequired,
  levelId: PropTypes.string.isRequired,
  configData: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

export default SSOConfigModal;
